// Pago en efectivo en ruta (pedido de Rodrigo, 2026-11-01): el vendedor marca lo que el cliente le paga,
// el ERP deja el comprobante CR-… y el pago queda en curso hasta que tesorería ingresa el efectivo.
// Exige señal: el comprobante lleva su número oficial al tiro. Reintentar no duplica (misma clave).
import { useState } from 'react';
import { api } from '../api/cliente';
import { nombreDte } from '../dte/pdfDte';
import { useSesion } from '../sesion/SesionContexto';
import type { VistaRecepcion } from '../tesoreria/comprobanteRuta';
import { nuevoIdExterno, type ClienteRuta, type Paquete } from './almacen';
import { useCola } from './cola';
import type { DocumentoCuenta } from './Cuenta';
import { pesos } from './logica';

const fecha = (iso: string) => `${iso.slice(8, 10)}-${iso.slice(5, 7)}-${iso.slice(2, 4)}`;
/** Lo que se puede abonar: lo que debe menos lo que ya está en curso. */
export const disponibleDe = (d: DocumentoCuenta) =>
  Math.max(0, Number(d.saldo) - Number(d.enCurso ?? 0));

export function CobroEfectivo({
  paquete,
  cliente: c,
  documentos,
  alCerrar,
  alRegistrar,
}: {
  paquete: Paquete;
  cliente: ClienteRuta;
  documentos: DocumentoCuenta[];
  alCerrar: () => void;
  alRegistrar: (v: VistaRecepcion) => void;
}) {
  const s = useSesion();
  const cola = useCola();
  const enLinea = s.estado === 'autenticado' && cola.enLinea;
  const cobrables = documentos.filter((d) => d.cxcId && disponibleDe(d) > 0);
  // Por defecto, la más antigua completa (lo usual: «me pagó la factura»).
  const [montos, setMontos] = useState<Record<string, string>>(() =>
    cobrables[0] ? { [cobrables[0].cxcId!]: String(disponibleDe(cobrables[0])) } : {},
  );
  const [observacion, setObservacion] = useState('');
  const [grabando, setGrabando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Una clave por pago: si se corta la señal y se reintenta, el ERP devuelve el mismo comprobante.
  const [idExterno] = useState(nuevoIdExterno);

  const elegidos = cobrables.filter((d) => montos[d.cxcId!] !== undefined);
  const problemas = elegidos.flatMap((d) => {
    const m = Number(montos[d.cxcId!] || 0);
    if (m <= 0) return [`${nombreDte(d.tipoDte)} ${d.folio}: ingresa el monto`];
    if (m > disponibleDe(d))
      return [`${nombreDte(d.tipoDte)} ${d.folio}: no puede pasar de ${pesos(disponibleDe(d))}`];
    return [];
  });
  const total = elegidos.reduce((t, d) => t + Number(montos[d.cxcId!] || 0), 0);

  const registrar = async () => {
    setGrabando(true);
    setError(null);
    try {
      const v = await api.post<VistaRecepcion>(
        `/vendedor/clientes/${c.entidadId}/cobros`,
        {
          empresaId: paquete.empresaId,
          ...(paquete.verComo ? { vendedorId: paquete.verComo } : {}),
          idExterno,
          documentos: elegidos.map((d) => ({
            cxcId: d.cxcId,
            monto: String(Number(montos[d.cxcId!])),
          })),
          observacion: observacion.trim() || null,
        },
        { idempotencia: `cobro-${idExterno}` },
      );
      alRegistrar(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGrabando(false);
    }
  };

  return (
    <div className="visor cobro" role="dialog" aria-modal="true" aria-label="Pago en efectivo">
      <div className="visor-barra">
        <button className="btn sec sm" type="button" onClick={alCerrar} disabled={grabando}>
          ✕ Cancelar
        </button>
        <div className="t">Pago en efectivo</div>
      </div>
      <div className="visor-cuerpo">
        <div className="caja">
          <h3>{c.nombreFantasia || c.razonSocial}</h3>
          <p className="h-sub">
            Marca lo que te paga y cuánto. Se le entrega un comprobante; el pago se formaliza cuando
            entregues el efectivo en tesorería.
          </p>
          {!cobrables.length ? (
            <div className="vacio">No tiene documentos con saldo por pagar.</div>
          ) : (
            cobrables.map((d) => {
              const id = d.cxcId!;
              const marcado = montos[id] !== undefined;
              return (
                <div key={id} className={`doc-cobro${marcado ? ' on' : ''}`}>
                  <label className="check-doc">
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={(e) =>
                        setMontos((m) => {
                          const n = { ...m };
                          if (e.target.checked) n[id] = String(disponibleDe(d));
                          else delete n[id];
                          return n;
                        })
                      }
                    />
                    <span>
                      <b>
                        {nombreDte(d.tipoDte)} {d.folio}
                      </b>
                      <small>
                        Vence {fecha(d.fechaVencimiento)} · debe {pesos(disponibleDe(d))}
                        {Number(d.enCurso) > 0 ? ` (más ${pesos(Number(d.enCurso))} en curso)` : ''}
                      </small>
                    </span>
                  </label>
                  {marcado && (
                    <div className="monto-doc">
                      <label htmlFor={`m-${id}`}>Recibe</label>
                      <input
                        id={`m-${id}`}
                        type="text"
                        inputMode="numeric"
                        value={montos[id]}
                        onChange={(e) =>
                          setMontos((m) => ({ ...m, [id]: e.target.value.replace(/\D/g, '') }))
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })
          )}
          <label className="et" htmlFor="cobro-obs">
            Nota (opcional)
          </label>
          <textarea
            id="cobro-obs"
            rows={2}
            maxLength={300}
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            placeholder="Ej.: pagó la señora en el local"
          />
          {problemas.length > 0 && (
            <div className="aviso" style={{ marginTop: 12 }}>
              {problemas.join(' · ')}
            </div>
          )}
          {error && <div className="error">{error}</div>}
          {!enLinea && (
            <div className="aviso" style={{ marginTop: 12 }}>
              Sin señal: para registrar el pago y entregar el comprobante necesitas conexión.
            </div>
          )}
        </div>
      </div>
      <div className="visor-acc">
        <div className="totales grande" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
          <span>Total en efectivo</span>
          <span>{pesos(total)}</span>
        </div>
        <button
          className="btn verde block"
          type="button"
          disabled={!enLinea || grabando || !elegidos.length || problemas.length > 0 || total <= 0}
          onClick={() => void registrar()}
        >
          {grabando ? 'Registrando…' : `Registrar pago de ${pesos(total)}`}
        </button>
      </div>
    </div>
  );
}
